import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad } from "@redotech/react-util/load";
import { Provider } from "@redotech/redo-model/order";
import {
  createContext,
  PropsWithChildren,
  useContext,
  useEffect,
  useState,
} from "react";
import { RedoAdminRpcClientContext } from "../app/redo-admin-rpc-client-provider";
import { TeamContext } from "../team/team";

type EcommerceProviderIntegrationContextType = {
  hasBigcommerceIntegration: boolean;
  hasShopifyIntegration: boolean;
  hasCommerceCloudIntegration: boolean;
  hasCommentSoldIntegration: boolean;
  primaryProvider: Provider | null;
  isLoading: boolean;
};

export const EcommerceProviderIntegrationContext =
  createContext<EcommerceProviderIntegrationContextType>({
    hasBigcommerceIntegration: false,
    hasShopifyIntegration: false,
    hasCommerceCloudIntegration: false,
    hasCommentSoldIntegration: false,
    primaryProvider: null,
    isLoading: true,
  });

export const EcommerceProviderIntegrationProvider = ({
  children,
}: PropsWithChildren) => {
  const rpcClient = useRequiredContext(RedoAdminRpcClientContext);
  const team = useContext(TeamContext);
  const [isLoading, setIsLoading] = useState(true);

  // State for integration flags and primary provider
  const [hasBigcommerceIntegration, setHasBigcommerceIntegration] =
    useState(false);
  const [hasShopifyIntegration, setHasShopifyIntegration] = useState(false);
  const [hasCommentSoldIntegration, setHasCommentSoldIntegration] =
    useState(false);
  const [hasCommerceCloudIntegration, setHasCommerceCloudIntegration] =
    useState(false);
  const [primaryProvider, setPrimaryProvider] = useState<Provider | null>(null);

  const teamId = team?._id;

  // Load team primary provider and integration statuses
  const teamProviderLoad = useLoad(
    (signal) =>
      teamId
        ? rpcClient.getTeamPrimaryProvider({ teamId }, { signal })
        : Promise.resolve({
            primaryProvider: null,
            hasShopifyIntegration: false,
            hasBigcommerceIntegration: false,
            hasCommentSoldIntegration: false,
            hasCommerceCloudIntegration: false,
          }),
    [rpcClient, teamId],
  );

  useEffect(() => {
    // Update integration state once the load is complete
    if (!teamProviderLoad.pending && teamProviderLoad.value) {
      const data = teamProviderLoad.value;

      setHasBigcommerceIntegration(data.hasBigcommerceIntegration);
      setHasShopifyIntegration(data.hasShopifyIntegration);
      setHasCommentSoldIntegration(data.hasCommentSoldIntegration);
      setHasCommerceCloudIntegration(data.hasCommerceCloudIntegration);
      setPrimaryProvider(data.primaryProvider);

      setIsLoading(false);
    }
  }, [teamProviderLoad.pending, teamProviderLoad.value]);

  return (
    <EcommerceProviderIntegrationContext.Provider
      value={{
        hasBigcommerceIntegration,
        hasShopifyIntegration,
        hasCommerceCloudIntegration,
        hasCommentSoldIntegration,
        primaryProvider,
        isLoading,
      }}
    >
      {children}
    </EcommerceProviderIntegrationContext.Provider>
  );
};
